// ═══════════════════════════════════════════════════════════════════════════
// BrandsTab — the brand vetting queue. Brands post deals immediately, but those
// deals stay hidden from hosts until a brand is APPROVED here (RLS, migration
// 075). Pending brands surface first, with their deals, contact, and advisory
// risk flags. Approve / reject / suspend; every action is audit-logged.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { listBrands, setBrandStatus, brandRiskFlags } from '../../lib/brandsAdmin';

const FILTERS = [
  { id: 'pending',   label: 'Pending' },
  { id: 'approved',  label: 'Approved' },
  { id: 'rejected',  label: 'Rejected' },
  { id: 'suspended', label: 'Suspended' },
  { id: null,        label: 'All' },
];

export default function BrandsTab() {
  const toast = useToast();
  const [filter, setFilter] = useState('pending');
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setBrands(await listBrands(filter)); }
    catch (e) { toast.error(e?.message || 'Could not load brands.'); }
    finally { setLoading(false); }
  }, [filter, toast]);

  useEffect(() => { refresh(); }, [refresh]);

  async function act(brand, status) {
    setBusyId(brand.user_id);
    try {
      await setBrandStatus(brand.user_id, status);
      toast.success(`${brand.brand_name || brand.full_name || 'Brand'} ${status}.`);
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'Could not update the brand.');
    } finally {
      setBusyId(null);
    }
  }

  const pendingCount = useMemo(
    () => brands.filter((b) => b.brand_status === 'pending').length,
    [brands]
  );

  return (
    <div className="brands-tab">
      <div className="brands-tab-head">
        <div>
          <h2 className="admin-section-title">Brand vetting</h2>
          <p className="admin-section-hint">
            Approve a brand and its live deals become matchable to hosts. Until then, hosts never see them.
          </p>
        </div>
      </div>

      <div className="brands-filters">
        {FILTERS.map((f) => (
          <button
            key={f.label}
            type="button"
            className={`brands-filter${filter === f.id ? ' is-on' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            {f.id === 'pending' && filter === 'pending' && pendingCount > 0 ? ` (${pendingCount})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="admin-muted">Loading…</p>
      ) : brands.length === 0 ? (
        <p className="admin-muted">No brands here.</p>
      ) : (
        <ul className="brands-list">
          {brands.map((b) => {
            const flags = brandRiskFlags(b);
            const busy = busyId === b.user_id;
            return (
              <li key={b.user_id} className="brand-row">
                <div className="brand-row-main">
                  <div className="brand-row-top">
                    <span className="brand-row-name">{b.brand_name || b.full_name || '(unnamed brand)'}</span>
                    <span className={`brand-row-status brand-row-status--${b.brand_status}`}>{b.brand_status}</span>
                    <span className="brand-row-deals">{b.deal_count} {Number(b.deal_count) === 1 ? 'deal' : 'deals'}</span>
                  </div>
                  <div className="brand-row-contact">
                    {b.contact_name && <span>{b.contact_name}</span>}
                    {b.contact_email && <span>{b.contact_email}</span>}
                    {!b.contact_email && b.email && <span>{b.email}</span>}
                  </div>
                  {flags.length > 0 && (
                    <div className="brand-row-flags">
                      {flags.map((f) => <span key={f} className="brand-flag">⚠ {f}</span>)}
                    </div>
                  )}
                  {(b.deals || []).length > 0 && (
                    <ul className="brand-deallist">
                      {b.deals.map((d) => (
                        <li key={d.id} className="brand-row-deal">
                          <span className={`brand-deal-type brand-deal-type--${d.deal_type}`}>{d.deal_type === 'free' ? 'Free' : 'Paid'}</span>
                          <span className="brand-deal-title">{d.title}</span>
                          {d.budget && <span className="brand-deal-budget">{d.budget}</span>}
                          <span className={`brand-deal-status brand-deal-status--${d.status}`}>{d.status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="brand-row-actions">
                  {b.brand_status !== 'approved' && (
                    <button type="button" className="brand-act brand-act--approve" disabled={busy} onClick={() => act(b, 'approved')}>Approve</button>
                  )}
                  {b.brand_status !== 'rejected' && (
                    <button type="button" className="brand-act brand-act--reject" disabled={busy} onClick={() => act(b, 'rejected')}>Reject</button>
                  )}
                  {b.brand_status === 'approved' && (
                    <button type="button" className="brand-act brand-act--suspend" disabled={busy} onClick={() => act(b, 'suspended')}>Suspend</button>
                  )}
                  {(b.brand_status === 'rejected' || b.brand_status === 'suspended') && (
                    <button type="button" className="brand-act" disabled={busy} onClick={() => act(b, 'pending')}>Re-queue</button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
